import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '@/context/AuthContext';
import '@/styles/PropertyPagesModern.scss';
import * as Sentry from '@sentry/react';

/**
 * EditPropertyModal Component
 * 
 * Modal dialog for editing property details with modern UI/UX and external system mappings.
 * Features:
 * - Form validation
 * - Loading states
 * - Modern toggle switch for status
 * - External system property mappings (MDA, Marketing, SIPA)
 * - Enhanced accessibility
 */
const EditPropertyModal = ({ property, onClose }) => {
    const { apiFetch, user } = useAuth();
    const [propertyName, setPropertyName] = useState(property.propertyName || property.PropertyName);
    const [comparativeName, setComparativeName] = useState(property.comparativeName || property.ComparativeName);
    const [shortCode, setShortCode] = useState(property.shortCode || property.ShortCode || '');
    const [active, setActive] = useState(property.active ?? property.Active);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // External mappings state
    const [externalMappings, setExternalMappings] = useState([]);
    const [loadingMappings, setLoadingMappings] = useState(true);
    const [showMappings, setShowMappings] = useState(false);

    const propertyID = property.propertyID || property.PropertyID;

    /**
     * Fetches external property mappings on component mount.
     */
    useEffect(() => {
        fetchExternalMappings();
    }, []);

    /**
     * Fetches external property mappings for this property.
     */
    const fetchExternalMappings = async () => {
        try {
            setLoadingMappings(true);
            const response = await apiFetch(`/api/Property/${propertyID}/external-mappings`);
            const data = await response.json();
            
            if (data.success && data.data) {
                setExternalMappings(data.data);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error fetching external mappings:', error);
        } finally {
            setLoadingMappings(false);
        }
    };

    /**
     * Handles form submission with validation.
     */
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validation
        if (!propertyName.trim()) {
            toast.error('Property Name is required');
            return;
        }

        setIsSubmitting(true);
        const userID = user.userID
        
        try {
            const payload = {
                propertyID: property.propertyID || property.PropertyID,
                propertyName,
                comparativeName,
                // Send null if shortCode is empty to maintain consistency with backend
                shortCode: shortCode && shortCode.trim().length > 0 ? shortCode.trim() : null,
                active,
                userID: userID
            };
            
            const response = await apiFetch('/api/Property/update', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            
            const result = await response.json();
            
            if (result.success) {
                toast.success('Property updated successfully');
                onClose();
            } else {
                toast.error(result.message || 'Failed to update property');
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error updating property:', error);
            toast.error('Network error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="modal-overlay-modern" onClick={onClose}>
            <div className="modal-content-modern" onClick={e => e.stopPropagation()}>
                <h2>Edit Property</h2>
                <form onSubmit={handleSubmit}>
                    <div className="form-group-modern">
                        <label htmlFor="propertyName">
                            Property Name<span className="required">*</span>
                        </label>
                        <input
                            type="text"
                            id="propertyName"
                            value={propertyName}
                            onChange={e => setPropertyName(e.target.value)}
                            placeholder="Enter property name"
                            required
                            disabled={isSubmitting}
                            aria-required="true"
                        />
                    </div>
                    
                    <div className="form-group-modern">
                        <label htmlFor="comparativeName">Comparative Name</label>
                        <input
                            type="text"
                            id="comparativeName"
                            value={comparativeName}
                            onChange={e => setComparativeName(e.target.value)}
                            placeholder="Enter comparative name (optional)"
                            disabled={isSubmitting}
                        />
                    </div>
                    
                    <div className="form-group-modern">
                        <label htmlFor="shortCode">
                            Short Code
                            <span className="field-description"> (e.g., ALX for Alexandra Plaza)</span>
                        </label>
                        <input
                            type="text"
                            id="shortCode"
                            value={shortCode}
                            onChange={e => {
                                // Only allow alphanumeric characters and convert to uppercase
                                const sanitizedValue = e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
                                setShortCode(sanitizedValue);
                            }}
                            placeholder="Enter short code (optional)"
                            disabled={isSubmitting}
                            maxLength={10}
                            style={{ textTransform: 'uppercase' }}
                        />
                    </div>
                    
                    <div className="toggle-group-modern">
                        <div className="toggle-label">
                            <span className="label-text">Property Status</span>
                            <span className="label-description">
                                {active ? 'Property is currently active' : 'Property is currently inactive'}
                            </span>
                        </div>
                        <label className="toggle-switch">
                            <input
                                type="checkbox"
                                checked={active}
                                onChange={e => setActive(e.target.checked)}
                                disabled={isSubmitting}
                                aria-label="Toggle property status"
                            />
                            <span className="slider-modern"></span>
                        </label>
                    </div>

                    {/* External System Mappings Section */}
                    <div className="form-section-divider"></div>
                    
                    <div className="external-mappings-section">
                        <button 
                            type="button"
                            className="btn-toggle-mappings"
                            onClick={() => setShowMappings(!showMappings)}
                            disabled={isSubmitting}
                        >
                            <span className="toggle-icon">{showMappings ? '▼' : '▶'}</span>
                            External System Mappings
                            {externalMappings.length > 0 && (
                                <span className="mapping-count">({externalMappings.length})</span>
                            )}
                        </button>

                        {showMappings && (
                            <div className="mappings-content">
                                {loadingMappings ? (
                                    <div className="loading-mappings">Loading mappings...</div>
                                ) : externalMappings.length === 0 ? (
                                    <div className="no-mappings">
                                        <p>No external system mappings configured.</p>
                                        <p className="help-text">
                                            External mappings link this property to MDA, Marketing, and SIPA databases for reporting.
                                        </p>
                                    </div>
                                ) : (
                                    <div className="mappings-list">
                                        {externalMappings.map((mapping) => (
                                            <div key={mapping.id} className="mapping-item">
                                                <div className="mapping-system">
                                                    <strong>{mapping.externalSystemDisplayName}</strong>
                                                    <span className={`mapping-status ${mapping.isActive ? 'active' : 'inactive'}`}>
                                                        {mapping.isActive ? '✓ Active' : '✗ Inactive'}
                                                    </span>
                                                </div>
                                                <div className="mapping-details">
                                                    <span className="mapping-label">External ID:</span>
                                                    <span className="mapping-value">{mapping.externalPropertyID}</span>
                                                </div>
                                                {mapping.externalPropertyName && (
                                                    <div className="mapping-details">
                                                        <span className="mapping-label">External Name:</span>
                                                        <span className="mapping-value">{mapping.externalPropertyName}</span>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <p className="help-text-small">
                                    To manage external mappings, please contact your system administrator.
                                </p>
                            </div>
                        )}
                    </div>
                    
                    <div className="modal-actions-modern">
                        <button 
                            type="button" 
                            className="btn-secondary" 
                            onClick={onClose}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </button>
                        <button 
                            type="submit" 
                            className="btn-primary"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? '💾 Saving...' : '💾 Save Changes'}
                        </button>
                    </div>
                </form>
            </div>

        </div>
    );
};

export default EditPropertyModal;